import { Component, EventEmitter, Input, OnDestroy, Output, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ProductService } from '../../pages/service/product.service';
import { Dialog, DialogModule } from 'primeng/dialog';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-ar-statement-form',
    standalone: true,
    imports: [CommonModule, FormsModule, AutoCompleteModule, ButtonModule, DialogModule],
    templateUrl: './ar-statement-form.component.html'
})
export class ArStatementFormComponent implements OnDestroy, AfterViewInit {
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Input() prefillCustomer?: {
        customerNumber: string;
        customerName: string;
    };

    // ViewChild references for focus management
    @ViewChild('customerName') customerNameRef!: AutoComplete;
    @ViewChild('customerNumber') customerNumberRef!: AutoComplete;
    @ViewChild('fromAccountingDateInput') fromAccountingDateInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('toAccountingDateInput') toAccountingDateInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('confirmButton') confirmButtonRef!: ElementRef<HTMLButtonElement>;

    /** Focus state for Request AR Statement button outline styling */
    confirmButtonFocused = false;
    ngOnChanges(changes: SimpleChanges): void {
        console.log('[AR] ngOnChanges detected:', changes);

        // Dialog opened
        if (changes['visible']?.currentValue === true) {
            console.info('[AR] Dialog opened');

            this.resetARDialog();

            const today = this.getToday();
            this.fromAccountingDate = today;
            this.toAccountingDate = today;

            // 👇 APPLY PREFILL AFTER RESET
            if (this.prefillCustomer) {
                this.applyPrefillCustomer(this.prefillCustomer);
            }

            // Focus first field (customer name) after dialog is visible
            this.focusFirstField();
        }

        // Prefill changed while dialog already open
        if (changes['prefillCustomer'] && changes['prefillCustomer'].currentValue) {
            console.info('[AR] Prefill customer received', changes['prefillCustomer'].currentValue);

            this.applyPrefillCustomer(changes['prefillCustomer'].currentValue);
        }
    }

    ngAfterViewInit(): void {
        // Focus management setup is handled in event handlers
    }

    /** Normalize to string; if object, extract known props (avoids [object Object] when parent passes object for name). */
    private normalizeToCustomerNumber(v: any): string {
        if (v == null) return '';
        if (typeof v === 'string') return v.trim();
        if (typeof v === 'object') return String(v.CUNO ?? v.customerId ?? v.id ?? '').trim();
        return String(v).trim();
    }

    private normalizeToCustomerName(v: any): string {
        console.log('normalizeToCustomerName', v);
        if (v == null) return '';
        if (typeof v === 'string') return v.trim();
        if (typeof v === 'object') return String(v.CUNM ?? v.customerName ?? v.name ?? '').trim();
        return String(v).trim();
    }

    private applyPrefillCustomer(customer: { customerNumber?: string; customerName?: string }) {
        if (!customer) return;

        const customerNumber = this.normalizeToCustomerNumber(customer.customerNumber);
        const customerName = this.normalizeToCustomerName(customer.customerName);
        if (!customerNumber) {
            return;
        }

        const c = {
            CUNO: customerNumber,
            CUNM: customerName || ''
        };

        this.customer = c;
        this.customerNumberObj = c;
        this.customerNumber = c.CUNO;
    }

    resetARDialog() {
        console.info('[AR] Resetting dialog');

        this.customer = null;
        this.customerNumberObj = null;
        this.customerNumber = null;

        this.customerOptions = [];
        this.customerNumberOptions = [];
    }

    // ---------- OUTPUT EVENTS ----------
    @Output() submitAR = new EventEmitter<{
        payerFrom: string;
        payerTo: string;
        accountingDateFrom: string;
        accountingDateTo: string;
    }>();

    @Output() cancelAR = new EventEmitter<void>();

    // ---------- CUSTOMER (single field; API still receives same value for payerFrom & payerTo) ----------
    customer: any = null;
    customerNumber: string | null = null;
    customerNumberObj: any = null;

    // ---------- AUTOCOMPLETE OPTIONS ----------
    customerOptions: any[] = [];
    customerNumberOptions: any[] = [];

    // ---------- DATES ----------
    fromAccountingDate: Date | null = null;
    toAccountingDate: Date | null = null;
    today: Date = new Date();

    /** For native date input: yyyy-MM-dd */
    get fromDateValue(): string {
        return this.dateToInputValue(this.fromAccountingDate);
    }
    get toDateValue(): string {
        return this.dateToInputValue(this.toAccountingDate);
    }
    get todayValue(): string {
        return this.dateToInputValue(this.today);
    }
    get toDateMax(): string {
        return this.dateToInputValue(this.toAccountingDate ?? this.today);
    }
    get toDateMin(): string {
        return this.dateToInputValue(this.fromAccountingDate);
    }

    private dateToInputValue(d: Date | null): string {
        if (!d) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    private inputValueToDate(s: string): Date | null {
        if (!s || typeof s !== 'string') return null;
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d;
    }

    onFromDateInput(value: string) {
        this.fromAccountingDate = this.inputValueToDate(value);
        this.onFromDateChange();
    }

    onToDateInput(value: string) {
        this.toAccountingDate = this.inputValueToDate(value);
        this.onToDateChange();
    }

    private destroy$ = new Subject<void>();

    constructor(private productService: ProductService) {}

    private getToday(): Date {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    // ================= FOCUS MANAGEMENT =================
    focusFirstField() {
        setTimeout(() => {
            if (this.customerNameRef?.inputEL?.nativeElement) {
                this.customerNameRef.inputEL.nativeElement.focus();
            }
        }, 250);
    }

    focusCustomerNumber() {
        setTimeout(() => {
            if (this.customerNumberRef?.inputEL?.nativeElement) {
                this.customerNumberRef.inputEL.nativeElement.focus();
            }
        }, 100);
    }

    focusFromDate() {
        setTimeout(() => {
            this.fromAccountingDateInputRef?.nativeElement?.focus();
        }, 200);
    }

    focusToDate() {
        setTimeout(() => {
            this.toAccountingDateInputRef?.nativeElement?.focus();
        }, 200);
    }

    focusConfirmButton() {
        setTimeout(() => {
            const btn = this.confirmButtonRef?.nativeElement;
            if (btn && !btn.disabled) {
                btn.focus();
            }
        }, 200);
    }

  

    // Handle Enter key on autocomplete
    onCustomerNameKeyDown() {
        this.focusCustomerNumber();
    }

    onCustomerNumberKeyDown() {
        this.focusFromDate();
    }

    // Handle Enter key on date fields: From Date → To Date, To Date → Request AR Statement button
    onDateKeyDown(type: 'FROM' | 'TO') {
        if (type === 'FROM') {
            this.focusToDate();
        } else {
            this.focusConfirmButton();
        }
    }

    // ================= CUSTOMER NAME =================
    filterCustomers(event: any) {
        const query = event.query?.trim();
        if (!query || query.length < 3) {
            this.customerOptions = [];
            return;
        }

        this.productService
            .searchCustomers(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                this.customerOptions = Array.isArray(res) ? res : res ? [res] : [];
            });
    }

    onCustomerSelect(event: any) {
        const c = event.value;
        this.customer = c;
        this.customerNumber = c.CUNO;
        this.customerNumberObj = c;
        this.focusCustomerNumber();
    }

    // ================= CUSTOMER NUMBER =================
    filterCustomerNumbers(event: any) {
        const query = event.query?.trim();
        if (!query || query.length < 3) {
            this.customerNumberOptions = [];
            return;
        }

        this.productService
            .searchCustomerNumbers(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe((res) => {
                this.customerNumberOptions = Array.isArray(res) ? res : res ? [res] : [];
            });
    }

    onCustomerNumberSelect(event: any) {
        const c = event.value;
        this.customer = c;
        this.customerNumber = c.CUNO;
        this.customerNumberObj = c;
        this.focusFromDate();
    }

    // ================= CLEAR LOGIC =================
    onCustomerCleared(value: any) {
        if (!value) {
            this.customer = null;
            this.customerNumber = null;
            this.customerNumberObj = null;
            this.customerOptions = [];
            this.customerNumberOptions = [];
        }
    }

    // ================= DATE VALIDATION =================
    onFromDateChange() {
        if (this.fromAccountingDate && this.toAccountingDate && this.fromAccountingDate > this.toAccountingDate) {
            this.toAccountingDate = null;
        }
        // Move focus to "To Date" after selecting from date
        this.focusToDate();
    }

    onToDateChange() {
        if (this.toAccountingDate && this.toAccountingDate > this.today) {
            this.toAccountingDate = null;
        }
        if (this.fromAccountingDate && this.toAccountingDate && this.toAccountingDate < this.fromAccountingDate) {
            this.toAccountingDate = null;
        }
        // Move focus to "Confirm Button" after selecting to date
        if (this.toAccountingDate) {
            this.focusConfirmButton();
        }
    }

    // ================= SUBMIT =================
    onConfirm() {
        if (!this.isFormValid()) return;

        this.submitAR.emit({
            payerFrom: this.customerNumber!,
            payerTo: this.customerNumber!,
            accountingDateFrom: this.formatDate(this.fromAccountingDate!),
            accountingDateTo: this.formatDate(this.toAccountingDate!)
        });
        this.closeDialogSafely();
    }

    closeDialogSafely() {
        setTimeout(() => {
            this.visibleChange.emit(false);
        });
    }

    onCancel() {
        this.resetForm();
        this.cancelAR.emit();
    }

    // ================= HELPERS =================
    isFormValid(): boolean {
        return !!(this.customerNumber && this.fromAccountingDate && this.toAccountingDate && this.fromAccountingDate <= this.toAccountingDate);
    }

    formatDate(date: Date): string {
        const y = date.getFullYear().toString().slice(-2);
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}${m}${d}`; // YYMMDD
    }

    resetForm() {
        this.customer = null;
        this.customerNumber = null;
        this.customerNumberObj = null;
        this.customerOptions = [];
        this.customerNumberOptions = [];
        this.fromAccountingDate = null;
        this.toAccountingDate = null;
    }

    closeDialog() {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    onDialogHide() {
        console.info('[AR FORM] Dialog closed');
        this.visibleChange.emit(false); // 🔴 THIS IS THE KEY
        this.resetARDialog();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}